/**
 * NOXCULT — WebGL Atmospheric Layer v2
 * nox-webgl.js
 *
 * FIXED vs v1:
 *  - Uses ResizeObserver to size canvas after layout is computed
 *  - Retries init if canvas is 0×0 (deferred paint)
 *  - Self-contained: no timing dependency on other scripts
 *  - Exposed immediately as window.NoxAtmosphere (no defer issue)
 */

(function (global) {
  'use strict';

  const VERT = `#version 300 es
precision mediump float;
in vec2 a_pos;
out vec2 v_uv;
void main(){
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

  const FRAG = `#version 300 es
precision mediump float;
uniform float u_time;
uniform float u_intensity;
uniform float u_mode;
uniform vec2  u_resolution;
uniform vec2  u_focus;
in vec2 v_uv;
out vec4 frag_color;

vec3 permute(vec3 x){ return mod(x*(x*34.0+1.0),289.0); }

float snoise(vec2 v){
  const vec4 C=vec4(0.211324865,0.366025403,-0.577350269,0.024390243);
  vec2 i=floor(v+dot(v,C.yy));
  vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
  vec4 x12=x0.xyxy+C.xxzz;
  x12.xy-=i1;
  i=mod(i,289.0);
  vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
  vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
  m=m*m;m=m*m;
  vec3 x=2.0*fract(p*C.www)-1.0;
  vec3 h=abs(x)-0.5;
  vec3 ox=floor(x+0.5);
  vec3 a0=x-ox;
  m*=1.79284291-0.85373472*(a0*a0+h*h);
  vec3 g;
  g.x=a0.x*x0.x+h.x*x0.y;
  g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.0*dot(m,g);
}

float fbm(vec2 p){
  float v=0.0; float a=0.5;
  vec2 shift=vec2(100.0);
  mat2 rot=mat2(cos(0.5),sin(0.5),-sin(0.5),cos(0.5));
  for(int i=0;i<4;i++){ v+=a*snoise(p); p=rot*p*2.1+shift; a*=0.48; }
  return v;
}

void main(){
  vec2 uv=v_uv;
  float ar=u_resolution.x/max(u_resolution.y,1.0);
  vec2 st=vec2(uv.x*ar,uv.y);
  float t=u_time*0.055;

  vec2 q;
  q.x=fbm(st+t);
  q.y=fbm(st+vec2(1.7,9.2)+t*0.85);
  vec2 r;
  r.x=fbm(st+2.8*q+vec2(1.7,9.2)+t*0.14);
  r.y=fbm(st+2.8*q+vec2(8.3,2.8)+t*0.11);
  float f=fbm(st+2.2*r);
  f=0.5+0.5*f;
  f=pow(max(f,0.0),2.0);

  vec2 vc=uv-0.5;
  float vgn=1.0-dot(vc,vc)*1.6;
  vgn=clamp(vgn,0.0,1.0);

  vec2 fc=uv-u_focus;
  fc.x*=ar;
  float fspot=exp(-dot(fc,fc)*4.5)*u_mode*0.45;

  vec3 col=mix(
    vec3(0.022,0.018,0.014),
    vec3(0.092,0.076,0.052),
    f*vgn
  );
  col+=vec3(0.16,0.12,0.08)*fspot;

  float alpha=(0.20*u_intensity+u_mode*0.05)*(f*0.65+0.35)*vgn;
  alpha=clamp(alpha,0.0,0.50);
  frag_color=vec4(col,alpha);
}`;

  class NoxAtmosphere {
    constructor(canvas, opts) {
      this.canvas      = canvas;
      this.opts        = Object.assign({ intensity: 0.5, fps: 28 }, opts || {});
      this._mode       = 0;
      this._intensity  = this.opts.intensity;
      this._focus      = [0.7, 0.5];
      this._raf        = null;
      this._last       = 0;
      this._interval   = 1000 / this.opts.fps;
      this._alive      = false;
      this._ro         = null;

      /* Defer init until canvas has layout dimensions */
      this._waitForSize();
    }

    _waitForSize() {
      /* ResizeObserver fires after first layout paint */
      if (typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => {
          if (this.canvas.offsetWidth > 0 && !this._alive) {
            this._ro.disconnect();
            this._init();
          } else if (this._alive) {
            this._resize();
          }
        });
        this._ro.observe(this.canvas);
      } else {
        /* Fallback: wait for DOMContentLoaded then rAF */
        const tryInit = () => {
          if (this.canvas.offsetWidth > 0) { this._init(); }
          else { requestAnimationFrame(tryInit); }
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', tryInit);
        } else {
          requestAnimationFrame(tryInit);
        }
      }
    }

    _init() {
      const gl = this.canvas.getContext('webgl2', {
        alpha: true, premultipliedAlpha: false,
        antialias: false, depth: false, stencil: false,
      });
      if (!gl) { console.warn('[NOX WebGL] WebGL2 not available'); return; }
      this.gl = gl;

      const prog = this._buildProgram();
      if (!prog) return;
      this.prog = prog;
      gl.useProgram(prog);

      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'a_pos');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      this._u = {
        time:       gl.getUniformLocation(prog, 'u_time'),
        intensity:  gl.getUniformLocation(prog, 'u_intensity'),
        mode:       gl.getUniformLocation(prog, 'u_mode'),
        resolution: gl.getUniformLocation(prog, 'u_resolution'),
        focus:      gl.getUniformLocation(prog, 'u_focus'),
      };

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

      this._alive = true;
      this._resize();
      this._tick(0);

      /* Keep canvas sized to container */
      if (!this._ro) {
        window.addEventListener('resize', () => this._resize(), { passive: true });
      }
    }

    _buildProgram() {
      const gl = this.gl;
      const compile = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
          console.warn('[NOX WebGL] shader:', gl.getShaderInfoLog(s));
          return null;
        }
        return s;
      };
      const vs = compile(gl.VERTEX_SHADER, VERT);
      const fs = compile(gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return null;
      const p = gl.createProgram();
      gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        console.warn('[NOX WebGL] link:', gl.getProgramInfoLog(p));
        return null;
      }
      return p;
    }

    _resize() {
      if (!this.gl) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w   = this.canvas.offsetWidth  || this.canvas.parentElement?.offsetWidth  || window.innerWidth;
      const h   = this.canvas.offsetHeight || this.canvas.parentElement?.offsetHeight || window.innerHeight;
      if (w === 0 || h === 0) return;
      this.canvas.width  = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }

    _tick(ts) {
      if (!this._alive) return;
      this._raf = requestAnimationFrame(t => this._tick(t));
      const elapsed = ts - this._last;
      if (elapsed < this._interval) return;
      this._last = ts - (elapsed % this._interval);
      const gl = this.gl;
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform1f(this._u.time,       ts * 0.001);
      gl.uniform1f(this._u.intensity,  this._intensity);
      gl.uniform1f(this._u.mode,       this._mode);
      gl.uniform2f(this._u.resolution, this.canvas.width, this.canvas.height);
      gl.uniform2f(this._u.focus,      this._focus[0], 1.0 - this._focus[1]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    setMode(mode) {
      const map = { idle: 0, hover: 1, active: 2 };
      this._mode = typeof mode === 'number' ? mode : (map[mode] ?? 0);
    }
    setIntensity(v) { this._intensity = Math.max(0, Math.min(1, v)); }
    setFocus(x, y)  { this._focus = [x, y]; }

    destroy() {
      this._alive = false;
      if (this._raf)  cancelAnimationFrame(this._raf);
      if (this._ro)   this._ro.disconnect();
    }
  }

  global.NoxAtmosphere = NoxAtmosphere;

})(window);
